import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, useScroll, useSpring } from "framer-motion";
import BrandLogo from "@/components/BrandLogo";
import { isAuthenticated } from "@/lib/api";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 220, damping: 40, mass: 0.3 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { pathname } = useLocation();
  const signedIn = isAuthenticated();
  // Section links scroll on the landing page and lead back to it from every other public page.
  const section = (hash) => (pathname === "/" ? hash : `/${hash}`);
  const links = [
    { href: section("#problem"), label: "The problem" },
    { href: section("#features"), label: "How we fix it" },
    { to: "/cohorts", label: "Cohorts" },
    { to: "/focus", label: "Focus timer" },
    { href: section("#pricing"), label: "Pricing" },
  ];
  const linkKey = (l) => l.to || l.href;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <BrandLogo />
          <span className="font-serif-editorial text-xl tracking-tight text-[#F5F5F0]">
            HustlrMate
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => l.to ? (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.slice(1)}`}
              className={`text-sm transition-colors font-light ${pathname.startsWith(l.to) ? "text-[#F5F5F0]" : "text-[#A1A1AA] hover:text-[#F5F5F0]"}`}
            >
              {l.label}
            </Link>
          ) : (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace(/^\/?#/, '')}`}
              className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors font-light"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {signedIn ? (
            <Link to="/dashboard" data-testid="nav-cta" className="hm-btn-primary px-4 py-2 rounded-md text-sm font-semibold">
              Open app
            </Link>
          ) : (
            <>
              <Link
                to="/auth?mode=signin"
                data-testid="nav-signin"
                className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors font-light"
              >
                Sign in
              </Link>
              <Link
                to="/auth"
                data-testid="nav-cta"
                className="hm-btn-primary px-4 py-2 rounded-md text-sm font-semibold"
              >
                Get started
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden text-[#F5F5F0] p-2"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <motion.div
        aria-hidden="true"
        className={`absolute left-0 right-0 bottom-0 h-px bg-[#F59E0B] origin-left transition-opacity duration-300 ${scrolled ? "opacity-80" : "opacity-0"}`}
        style={{ scaleX: progress }}
      />

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-white/5 bg-black/90 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => l.to ? (
              <Link
                key={linkKey(l)}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] font-light"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={linkKey(l)}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] font-light"
              >
                {l.label}
              </a>
            ))}
            <div className="h-px bg-white/5 my-2" />
            {signedIn ? (
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="hm-btn-primary px-4 py-2 rounded-md text-sm font-semibold text-center"
              >
                Open app
              </Link>
            ) : (
              <>
                <Link
                  to="/auth?mode=signin"
                  onClick={() => setOpen(false)}
                  className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0] font-light"
                >
                  Sign in
                </Link>
                <Link
                  to="/auth"
                  onClick={() => setOpen(false)}
                  className="hm-btn-primary px-4 py-2 rounded-md text-sm font-semibold text-center"
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
